import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import { useEffect } from 'react';

export interface Contest {
  id: string;
  name: string;
  description: string;
  slug: string;
  start_date: string;
  end_date: string;
  status: 'draft' | 'active' | 'completed' | 'cancelled';
  vote_price: number;
  logo_url?: string;
  created_at: string;
  updated_at: string;
  user_id: string;
}

export const useContests = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: contests, isLoading, error } = useQuery<Contest[]>({
    queryKey: ['contests'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    }
  });

  useEffect(() => {
    const checkAndUpdateContestsStatus = async () => {
      if (!contests || contests.length === 0) return;

      const now = new Date();
      let updatedContests = false;
      
      // Vérifier les concours expirés (active -> completed)
      const expiredContests = contests.filter(
        c => c.status === 'active' && new Date(c.end_date) < now
      );
      
      if (expiredContests.length > 0) {
        console.log(`Mise à jour de ${expiredContests.length} concours expirés`);
        
        for (const contest of expiredContests) {
          const { error } = await supabase
            .from('contests')
            .update({ status: 'completed' })
            .eq('id', contest.id);
            
          if (error) {
            console.error(`Erreur lors de la mise à jour du concours ${contest.id}:`, error);
          } else {
            updatedContests = true;
          }
        }
      }
      
      // Vérifier les concours qui doivent démarrer (draft -> active)
      const startingContests = contests.filter(
        c => c.status === 'draft' && new Date(c.start_date) <= now
      );
      
      if (startingContests.length > 0) {
        console.log(`Activation de ${startingContests.length} concours qui débutent`);
        
        for (const contest of startingContests) {
          const { error } = await supabase
            .from('contests')
            .update({ status: 'active' })
            .eq('id', contest.id);
            
          if (error) {
            console.error(`Erreur lors de l'activation du concours ${contest.id}:`, error);
          } else {
            updatedContests = true;
          }
        }
      }
      
      // Si des concours ont été mis à jour, rafraîchir les données
      if (updatedContests) {
        queryClient.invalidateQueries({ queryKey: ['contests'] });
      }
    };

    checkAndUpdateContestsStatus();
  }, [contests, queryClient]);

  return {
    contests,
    isLoading,
    error,
  };
};
